/**
 * Implementation of general settings component accessible at /dashboard/settings/
 *
 * @author - Harsh Tomar
 */

// Dependencies
import { useQuery } from "react-query";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useProfile, useUpdateUserName, useUpdateAlerts } from "../../api/reactQuery";
import { getMyProfile } from "../../api/requests";
import SettingCard, { Field } from "../../components/SettingCard";
import SettingsItem from "../../components/SettingsItem";
import { When } from "react-if";

export default function GeneralSettings() {
  const websiteSettings = {
    websiteName: "Moqupp",
    websiteUrl: "https://moqupp.com",
    themeColor: "#0088cc",
    notionPageUrl: "https://notion.so/123-lsad=12312",
  };

  const { data : profile, refetch } = useQuery(["profile"],()=>getMyProfile(), { enabled: false})

  const alertToFromProfile =  profile?.alertTo


  const accountSettings = {
    name: profile?.name || 'No Name',
    email: profile?.email,
  };

  const { mutate: updateUser ,isLoading: isUpdateUserProcessing } = useUpdateUserName();

  const { mutate: updateAlerts ,isLoading: isAlertsUpdating } = useUpdateAlerts();

  const [websiteEditing, setWebsiteEditing] = useState(false);
  const [accountEditing, setAccountEditing] = useState(false);
  const [alertEditing, setAlertEditing] = useState(false);
  const [alertTo, setAlertTo] = useState('');
  const [alertToRaw, setAlertToRaw] = useState('');
  const [errorAlertTo, setErrorAlertTo] = useState('');


  const [websiteName, setWebsiteName] = useState(websiteSettings.websiteName);
  const [websiteUrl, setWebsiteUrl] = useState(websiteSettings.websiteUrl);
  const [themeColor, setThemeColor] = useState(websiteSettings.themeColor);
  const [notionUrl, setNotionUrl] = useState(websiteSettings.notionPageUrl);

  const [name, setName] = useState(accountSettings.name);

  useEffect(()=>{
    if(alertToFromProfile){
    const alertToCommaseperated = alertToFromProfile.reduce((acc:string,a:Record<string,string>,index:number)=>{
      return acc + (index !== 0 ? "," : '')+a?.to
    },'')

    setAlertTo(alertToCommaseperated);
    setAlertToRaw(alertToCommaseperated)
  }
  },[alertToFromProfile]);

  const onChangeAlertTo = useCallback((e:ChangeEvent<HTMLInputElement>)=>{
      const newAlertToRaw = e.target.value
      setAlertToRaw(newAlertToRaw);
      setErrorAlertTo('');
      if(!newAlertToRaw){
        return;
      }

      setAlertTo(newAlertToRaw);

  },[])

  const onSaveAlterTo = useCallback(()=>{
    const alertArrayRaw = alertTo.trim().split(',').map(al=>al.trim());
    const isValidAlertTo = alertArrayRaw.every(al=>{
      return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(al)
    })
    if(isValidAlertTo){
      // mutate here
      console.log(alertArrayRaw);
      updateAlerts({ alertTo: alertArrayRaw.map(a=>{
        return {
          to: a,
          type:'EMAIL'
        }
      }) },{
        onSuccess:()=>{
          refetch();
        }
      })
      setAlertEditing(false)
      
    }
    else {
      setErrorAlertTo('Please specify valid emails separated by commas. The current input is not a valid email address')
    }
  },[alertTo])
  
  return (
    <div className="p-3 max-w-4xl">
      {/* Website Settings Begin */}
      {/* <SettingCard
        heading="Website Settings"
        onEditClick={() => setWebsiteEditing(true)}
        onEditCancel={() => setWebsiteEditing(false)}
      >
        <SettingsItem label="Website Name">
          {websiteEditing ? (
            <input
              type="text"
              className="input-field-primary"
              value={websiteName}
              onChange={(e) => setWebsiteName(e.target.value)}
              required
            />
          ) : (
            <p className="text-lg">{websiteSettings.websiteName}</p>
          )}
        </SettingsItem>
        <SettingsItem label="Website Domain">
          {websiteEditing ? (
            <input
              type="text"
              className="input-field-primary"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          ) : (
            <p className="text-lg">{websiteSettings.websiteUrl}</p>
          )}
        </SettingsItem>
        <SettingsItem label="Brand Color">
          <span
            className="w-6 h-6 rounded-lg inline-block align-middle mr-3"
            style={{ backgroundColor: "#0088cc" }}
          ></span>
          {websiteEditing ? (
            <input
              type="color"
              className="input-field-primary"
              value={themeColor}
              onChange={(e) => setThemeColor(e.target.value)}
            />
          ) : (
            <p className="text-lg inline-block">{websiteSettings.themeColor}</p>
          )}
        </SettingsItem>
        <SettingsItem label="Notion Page Url">
          {websiteEditing ? (
            <input
              type="text"
              className="input-field-primary"
              value={notionUrl}
              onChange={(e) => setNotionUrl(e.target.value)}
            />
          ) : (
            <p className="text-lg">{websiteSettings.notionPageUrl}</p>
          )}
        </SettingsItem>
      </SettingCard> */}
      {/* Website Settings End */}

      {/* Communication Setting Start */}
      <SettingCard
        heading="Alert Settings"
        editing={alertEditing}
        onEditClick={() => setAlertEditing(true)}
        onEditCancel={() => setAlertEditing(false)}
        onEditSave = {()=>{
          onSaveAlterTo()

        }}
      >
        <SettingsItem label="Send E-mail alerts to" >
          {alertEditing ? (
            <>
            <input
              type="text"
              className="input-field-primary w-full"
              value={alertToRaw}
              onChange={(e) => onChangeAlertTo(e)}
            />
            <p className="text-gray-400">Comma Seperated</p>
            <When condition={errorAlertTo}>
          <div>
            <h3 className="text-red-500 text-base font-bold">{errorAlertTo}</h3>
          </div>
        </When>
            </>
          ) : (
            <p className="text-lg">{alertTo}</p>
          )}
        </SettingsItem>
      </SettingCard>
      {/* Communication Setting End */}

      {/* Account Settings Start */}
      <SettingCard
        heading="Account Settings"
        editing={accountEditing}
        onEditClick={() => setAccountEditing(true)}
        onEditCancel={() => setAccountEditing(false)}
        onEditSave = {()=>{
          updateUser(name, {
            onSuccess:()=>{
              setAccountEditing(false);
              refetch();
            }
          })
        }}
      >
        <SettingsItem label="Name">
          {accountEditing ? (
            <input
              type="text"
              className="input-field-primary"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : (
            <p className="text-lg">{accountSettings.name}</p>
          )}
        </SettingsItem>
        <SettingsItem label="Email">
          <p className="text-lg">{accountSettings.email}</p>
        </SettingsItem>
      </SettingCard>
      {/* Account Settings End */}
      
    </div>
  );
}
